import React, { createContext, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  initializeSocket,
  getSocket,
  disconnectSocket,
  subscribeToOrders,
  subscribeToInventory,
  subscribeToDashboard,
} from "../services/socketService";

const SocketContext = createContext(null);

export const useSocketContext = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocketContext must be used within SocketProvider");
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token) || localStorage.getItem("authToken");

  useEffect(() => {
    // Only connect if user is logged in
    if (token && user) {
      console.log("🔌 SocketContext: Initializing socket connection...");
      console.log("🔌 SocketContext: User:", user?.email || user?.id);
      console.log("🔌 SocketContext: Token exists:", !!token);
      
      const socketInstance = initializeSocket(token);
      setSocket(socketInstance); // Set socket immediately so components can use it

      // Set up connection status listeners
      socketInstance.on("connect", () => {
        console.log("✅ SocketContext: Socket connected successfully:", socketInstance.id);
        setIsConnected(true);

        // Auto-subscribe based on user role
        setTimeout(() => {
          const userType = user?.user_type;
          if (userType === "admin" || userType === "super_admin") {
            subscribeToOrders();
            subscribeToInventory();
            subscribeToDashboard();
          } else if (userType === "vendor") {
            subscribeToInventory();
          }
        }, 100); // Small delay to ensure connection is fully established
      });

      socketInstance.on("connected", (data) => {
        console.log("✅ SocketContext: Socket connection confirmed by server:", data);
        setIsConnected(true);
      });

      socketInstance.on("connect_error", (error) => {
        console.error("❌ SocketContext: Socket connection error:", error.message);
        setIsConnected(false);
      });

      socketInstance.on("disconnect", (reason) => {
        console.log("❌ SocketContext: Socket disconnected. Reason:", reason);
        setIsConnected(false);
      });

      // Cleanup on unmount
      return () => {
        console.log("🔌 SocketContext: Cleaning up socket connection...");
        disconnectSocket();
        setIsConnected(false);
        setSocket(null);
      };
    } else {
      // Disconnect if user logs out
      console.log("🔌 SocketContext: No token or user, disconnecting...");
      disconnectSocket();
      setIsConnected(false);
      setSocket(null);
    }
  }, [token, user]);

  const value = {
    socket,
    isConnected,
    subscribeToOrders,
    subscribeToInventory,
    subscribeToDashboard,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};

